import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, PlusCircle, FilterIcon, ArrowLeft, Building2 } from 'lucide-react';
import { useSearchParams, Link } from 'react-router-dom';
import AppliedFilters from '@/components/AppliedFilters';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import DynamicFormModal from '@/components/DynamicFormModal';
import { useToast } from '@/components/ui/use-toast';
import { clientsColumns, columnsExcel } from '@/pages/parametrizacion/clients/utils/clientsColumns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fetchClients, createClient, updateClient, deleteClient, exportClients } from '@/pages/parametrizacion/clients/Services/clients.services';
import FilterDrawer from '@/components/FilterDrawer';
import ExportExcelButton from '@/components/ExportExcelButton';
import AssignGarmentsModal from './components/AssignGarmentsModal';
import GarmentPricesModal from './components/GarmentPricesModal';
import ClientDocumentsModal from './components/ClientDocumentsModal';


const DEFAULT_FILTERS = { estado: 'Activo' };

const ClientsPage = () => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const idLavanderia = searchParams.get('lavanderia');
  const [laundryName, setLaundryName] = useState('');

  const [displayedData, setDisplayedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState(
    idLavanderia ? { ...DEFAULT_FILTERS, 'lavanderia.id': idLavanderia } : DEFAULT_FILTERS
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [laundriesOptions, setLaundriesOptions] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [modalMode, setModalMode] = useState('create');

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [isAssignGarmentsModalOpen, setIsAssignGarmentsModalOpen] = useState(false);
  const [clientForGarments, setClientForGarments] = useState(null);

  const [isGarmentPricesModalOpen, setIsGarmentPricesModalOpen] = useState(false);
  const [clientForPrices, setClientForPrices] = useState(null);

  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);
  const [clientForDocuments, setClientForDocuments] = useState(null);

  const fetchAndSetClients = useCallback(async () => {
    setIsLoading(true);
    try {
      const offset = currentPage;
      const { data, totalRecords } = await fetchClients({ limit: itemsPerPage, offset, query: filters });
      setDisplayedData(data);
      setTotalItems(totalRecords);
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'No se pudieron obtener los clientes.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, filters, toast]);

  useEffect(() => {
    fetchAndSetClients();
  }, [fetchAndSetClients]);

  // Fetch laundry name if coming from CentroParametrizacionPage
  useEffect(() => {
    if (idLavanderia) {
      const fetchLaundryName = async () => {
        try {
          const { fetchLaundryById } = await import('@/pages/parametrizacion/laundries/Services/laundries.services');
          const laundry = await fetchLaundryById(idLavanderia);
          setLaundryName(laundry?.nombre || '');
        } catch (e) {
          console.error('Error fetching laundry:', e);
        }
      };
      fetchLaundryName();
    }
  }, [idLavanderia]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { fetchLaundries } = await import('@/pages/parametrizacion/laundries/Services/laundries.services');
        const response = await fetchLaundries({ limit: 100, offset: 1, query: { estado: 'Activo' } });
        if (!mounted) return;
        setLaundriesOptions(response.data.map(laundry => ({ value: laundry._id, label: laundry.nombre })));
      } catch (error) {
        console.error('Error cargando centros de lavado:', error);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleLimitChange = (newLimit) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1);
  };

  const handleOpenModal = (mode, item = null) => {
    setModalMode(mode);
    setCurrentItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentItem(null);
  };

  const handleSaveItem = async (itemData) => {
    try {
      if (modalMode === 'create') {
        const response = await createClient(itemData);
        if (response.code === 201) {
          toast({ title: "Cliente creado", description: "El cliente fue registrado correctamente." });
        } else {
          throw new Error(response.message || "Error al crear cliente");
        }
      } else if (modalMode === 'edit') {
        const response = await updateClient(currentItem._id, itemData);
        if (response.code === 200) {
          toast({ title: "Cliente actualizado", description: "Los datos fueron actualizados correctamente." });
        } else {
          throw new Error(response.message || "Error al actualizar cliente");
        }
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Ocurrió un problema al guardar el cliente.",
        variant: "destructive",
      });
    } finally {
      handleCloseModal();
      fetchAndSetClients();
    }
  };

  const handleDeleteConfirmation = (item) => {
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteItem = async () => {
    try {
      const response = await deleteClient(itemToDelete._id);
      if (response.code === 200) {
        toast({ title: "Cliente eliminado", description: "El registro fue eliminado correctamente." });
        fetchAndSetClients();
      } else {
        throw new Error(response.message || "Error al eliminar el cliente");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Ocurrió un problema al eliminar el cliente.",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleAction = (actionType, row) => {
    if (actionType === 'edit') {
      handleOpenModal('edit', row);
    } else if (actionType === 'delete') {
      setItemToDelete(row);
      setIsDeleteDialogOpen(true);
    } else if (actionType === 'assignGarments') {
      setClientForGarments(row);
      setIsAssignGarmentsModalOpen(true);
    } else if (actionType === 'garmentPrices') {
      setClientForPrices(row);
      setIsGarmentPricesModalOpen(true);
    } else if (actionType === 'attachments') {
      setClientForDocuments(row);
      setIsDocumentsModalOpen(true);
    }
  };

  const handleAssignGarmentsSuccess = () => {
    toast({
      title: 'Éxito',
      description: 'Prendas asignadas correctamente',
    });
    setIsAssignGarmentsModalOpen(false);
    setClientForGarments(null);
  };

  const handleFilterChange = (newFilters) => {
    setCurrentPage(1);
    setFilters(newFilters);
  };

  const handleRemoveFilter = (key) => {
    const newFilters = { ...filters };
    delete newFilters[key];
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const filterFields = useMemo(() => [
    {
      id: 'nombre',
      label: 'Nombre del Cliente',
      type: 'text',
      placeholder: 'Buscar por nombre...',
    },
    {
      id: 'lavanderia.id',
      label: 'Centro de Lavado',
      type: 'select',
      options: laundriesOptions,
    },
  ], [laundriesOptions]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="p-4 md:p-6"
    >
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {idLavanderia && (
              <Button asChild variant="outline" size="icon" className="shrink-0">
                <Link to={`/parametrizacion/centros/${idLavanderia}`}>
                  <ArrowLeft className="h-5 w-5" />
                </Link>
              </Button>
            )}
            <div className="bg-primary/10 p-3 rounded-lg">
              <ShoppingCart className="h-8 w-8 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl">
                Parametrización de Clientes
                {laundryName && (
                  <span className="text-sm font-normal text-muted-foreground ml-2">
                    — {laundryName}
                  </span>
                )}
              </CardTitle>
              <CardDescription>
                {idLavanderia
                  ? 'Gestiona los clientes del centro de lavado.'
                  : 'Gestiona los clientes de la empresa.'}
              </CardDescription>
            </div>
          </div>

          <div className="w-full md:w-auto flex flex-col md:flex-row gap-3">
            <div className="flex flex-wrap gap-2 justify-end">
              <ExportExcelButton
                title="Clientes"
                service={async ({ query }) => exportClients(query || filters)}
                filters={filters}
                columns={columnsExcel}
                fileName="Clientes"
              />
              <Button onClick={() => handleOpenModal('create')}>
                <PlusCircle className="mr-2 h-4 w-4" /> Crear Cliente
              </Button>
              <Button
                onClick={() => setIsDrawerOpen(true)}
                variant="outline">
                <FilterIcon className="mr-2 h-4 w-4" variant="outline" size={20} />
                Filtros
              </Button>
            </div>
          </div>

        </CardHeader>
        <CardContent>
          <div className="w-full">
            <AppliedFilters
              filters={filters}
              fields={filterFields}
              onRemoveFilter={handleRemoveFilter}
            />
          </div>
          <DataTable
            data={displayedData}
            columns={clientsColumns}
            isLoading={isLoading}
            onAction={handleAction}
            page={currentPage}
            limit={itemsPerPage}
            totalRecords={totalItems}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
          />
          <FilterDrawer
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            fields={filterFields}
            initialFilters={filters}
            onChange={handleFilterChange}
            onApply={handleFilterChange}
            initialDay={false}
          />
        </CardContent>
      </Card>

      <AnimatePresence>
        {isModalOpen && (
          <DynamicFormModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            onSave={handleSaveItem}
            item={currentItem}
            fields={clientsColumns}
            title={currentItem ? 'Editar Cliente' : 'Crear Nuevo Cliente'}
          />
        )}
      </AnimatePresence>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro de eliminar este cliente?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente el cliente
              "{itemToDelete?.nombre}" de tus registros.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setItemToDelete(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteItem} className="bg-destructive hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AssignGarmentsModal
        isOpen={isAssignGarmentsModalOpen}
        onClose={() => {
          setIsAssignGarmentsModalOpen(false);
          setClientForGarments(null);
        }}
        client={clientForGarments}
        onSuccess={handleAssignGarmentsSuccess}
      />

      <GarmentPricesModal
        isOpen={isGarmentPricesModalOpen}
        onClose={() => {
          setIsGarmentPricesModalOpen(false);
          setClientForPrices(null);
        }}
        client={clientForPrices}
        onSuccess={() => {
          toast({ title: 'Éxito', description: 'Precios por prenda actualizados correctamente' });
          setIsGarmentPricesModalOpen(false);
          setClientForPrices(null);
        }}
      />

      <ClientDocumentsModal
        isOpen={isDocumentsModalOpen}
        onClose={() => {
          setIsDocumentsModalOpen(false);
          setClientForDocuments(null);
        }}
        client={clientForDocuments}
      />
    </motion.div>
  );
};

export default ClientsPage;
